import type { Units } from '@turf/turf';
import * as turf from '@turf/turf';
import { subscribeKey } from 'valtio/utils';
import { calculateArea, calculateLength } from '~/helpers/unitHelpers';
import { areaStore } from '../../Widgets/Measure/store';
import type { CanvasEvent } from '../CanvasEvent';
import DrawPolygonObject from '../polygon/DrawPolygonObject';
import type SurfaceBase from '../SurfaceBase';
import type { Point } from '../SurfaceBase';
import VertexObject from '../VertexObject';
import {
  fontSize,
  getSegmentData,
  measureColor,
  measureFillColor,
  measureLineDash,
  measureLineWidth,
  padding,
} from './common';

export default class DrawMeasureAreaObject extends DrawPolygonObject {
  constructor(id: string, surface: SurfaceBase, onMeasureArea: () => void) {
    super(id, surface, onMeasureArea);

    subscribeKey(areaStore, 'unitType', () => this.renderAll());
  }

  get area() {
    const { unitType, unitSystem } = areaStore;
    return calculateArea({
      unitType,
      unitSystem,
      geometry: {
        type: 'Polygon',
        coordinates: [
          [
            ...this.vertices.map((v) => [v.lngLat.lng, v.lngLat.lat]),
            [this.pointerVertex.lngLat.lng, this.pointerVertex.lngLat.lat],
            [this.vertices[0].lngLat.lng, this.vertices[0].lngLat.lat],
          ],
        ],
      },
    });
  }

  get length() {
    const { unitType, unitSystem } = areaStore;
    const vFirst = this.vertices[0];
    const units: Units = unitType === 'acres' ? 'feet' : (unitType as Units);
    return calculateLength({
      unitType: units,
      unitSystem,
      geometry: {
        type: 'Polygon',
        coordinates: [
          [
            ...this.vertices.map((v) => [v.lngLat.lng, v.lngLat.lat]),
            [this.pointerVertex.lngLat.lng, this.pointerVertex.lngLat.lat],
            [vFirst.lngLat.lng, vFirst.lngLat.lat],
          ],
        ],
      },
    });
  }

  getTextTranslation = (point: Point): boolean => {
    const { lng, lat } = this.surface.map.unproject([point.x, point.y]);
    const p = turf.point([lng, lat]);

    const vertices = [
      ...this.vertices.map((v) => [v.lngLat.lng, v.lngLat.lat]),
      [this.pointerVertex.lngLat.lng, this.pointerVertex.lngLat.lat],
    ];

    vertices.push(vertices[0]);

    if (vertices.length < 4) return false;

    const polygon = turf.polygon([vertices]);

    return turf.booleanPointInPolygon(p, polygon);
  };

  renderSegmentText = (ctx: CanvasRenderingContext2D): void => {
    for (let i = 1; i <= this.vertices.length + 1; i++) {
      let v1: VertexObject;
      let v2: VertexObject;

      if (i === this.vertices.length) {
        v1 = this.vertices[this.vertices.length - 1];
        v2 = this.pointerVertex;
      } else if (i === this.vertices.length + 1) {
        v1 = this.pointerVertex;
        v2 = this.vertices[0];
      } else {
        v1 = this.vertices[i - 1];
        v2 = this.vertices[i];
      }

      const { unitType, unitSystem } = areaStore;
      const units: Units = unitType === 'acres' ? 'feet' : (unitType as Units);
      const s = getSegmentData(v1, v2, units, unitSystem);

      const w = v2.point.x - v1.point.x;
      const h = v2.point.y - v1.point.y;

      const segmentCanvasLength = Math.hypot(w, h);

      ctx.save();
      ctx.font = `bold ${fontSize}px arial`;
      const lengthText = `${s.length.value.toLocaleString()} ${s.length.unit}`;
      const textWidth = ctx.measureText(lengthText).width;

      let textYOffset: number = -fontSize - 2 * padding;

      if (this.getTextTranslation(s.point)) {
        textYOffset = fontSize;
      }

      if (textWidth + padding <= segmentCanvasLength) {
        ctx.fillStyle = measureColor;
        ctx.translate(s.point.x, s.point.y);
        ctx.rotate(s.rotation);

        ctx.beginPath();
        ctx.roundRect(-textWidth / 2 - padding / 2, textYOffset, textWidth + padding, fontSize + padding, [5]);

        ctx.fill();

        ctx.textBaseline = 'top';
        ctx.textAlign = 'center';
        ctx.fillStyle = 'white';
        ctx.fillText(`${s.length.value.toLocaleString()} ${s.length.unit}`, 0, textYOffset + padding / 2);
      }

      ctx.restore();
    }
  };

  onSurfaceTap = (e: CanvasEvent) => {
    if (e.type === 'dblTap') return;

    const v = new VertexObject(`v-${this.vertices.length}`, this.surface, {
      point: e.point,
      lngLat: e.lngLat,
    });

    this.vertices.push(v);
    this.surface.addObject(v);
  };

  calculateTextPoint = (tw: number, th: number): Point => {
    const { x, y } = this.centroid();
    const p = { x: x - tw / 2, y: y - th / 2 };
    return p;
  };

  render(ctx: CanvasRenderingContext2D): void {
    if (this.vertices.length === 0) return;

    // change strokeStyle before ctx.save() so it changes the vertices' colors as well.
    ctx.strokeStyle = measureColor;
    ctx.save();
    ctx.lineWidth = measureLineWidth;
    ctx.setLineDash(measureLineDash);
    const { point } = this.vertices[0];
    ctx.moveTo(point.x, point.y);

    for (let i = 1; i < this.vertices.length; i++) {
      const p = this.vertices[i].point;
      ctx.lineTo(p.x, p.y);
    }

    ctx.lineTo(this.pointerVertex.point.x, this.pointerVertex.point.y);
    ctx.closePath();
    ctx.stroke();

    ctx.fillStyle = measureFillColor;
    ctx.fill();

    const a = this.area;
    areaStore.area = a.value;

    const l = this.length;
    areaStore.length = l.value;

    ctx.font = `bold ${fontSize}px arial`;
    const areaText = `A: ${a.value.toLocaleString()} ${a.unit}`;
    const lengthText = `P: ${l.value.toLocaleString()} ${l.unit}`;

    const textWidth = Math.max(ctx.measureText(areaText).width, ctx.measureText(lengthText).width);

    const textPoint = this.calculateTextPoint(textWidth, fontSize);

    ctx.fillStyle = measureColor;
    ctx.beginPath();
    ctx.roundRect(
      textPoint.x - padding / 2,
      textPoint.y - padding / 2,
      textWidth + padding,
      2 * fontSize + 2 * padding,
      [5],
    );
    ctx.fill();
    ctx.textBaseline = 'top';
    ctx.fillStyle = 'white';
    ctx.fillText(`${areaText}`, textPoint.x, textPoint.y);

    ctx.fillText(`${lengthText}`, textPoint.x, textPoint.y + fontSize + padding);
    ctx.restore();

    this.renderSegmentText(ctx);
  }
}
